import { IconButton, TextButton } from '@creditornot/cb-button';
import { BarcodeTag, ChainLink, Cross, MagnifyingGlass } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars, typographyCss, WoltLogo } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { searchCatalogProducts } from 'apis/offer-listing-service/api';
import { Product } from 'apis/offer-listing-service/types';
import { NutritionSetting } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { useDebounce } from 'common/hooks';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';

import { BaseField } from './types';
import { useProductOverridesContext } from '../../../context/ProductOverridesContext';
import { FormRowRenderer } from '../FormRowRenderer';
import { DisplayText, StyledInputContainer } from '../OfferDetailsFormFields.styles';
import { transformFeedProductToFormValues } from '../transformers';
import { ProductFormValues, FormValues } from '../types';
import { getCombinedFormValues } from '../utils';

type CatalogProductInfoProps = {
  product: Product;
  onDisconnect?: () => void;
  menuLocalizer: (localized: Localized | undefined, strict?: boolean) => string;
  isPreview?: boolean;
};

type ProductFieldsProps = BaseField & {
  nutritionSettings: NutritionSetting[];
};

export const PreviewRowContainer = styled.div`
  flex: 1;
`;

const SpinnerContainer = styled.div`
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledPopover = styled(LegacyPopover)`
  width: 100%;
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  width: 354px;
  padding: 0 8px;
  max-height: 300px;
  overflow: auto;
`;

const StyledMenuActionItem = styled(MenuActionItem)`
  padding: 0;
`;

const StyledProductContainer = styled.div<{ $showBorder?: boolean; $showPadding?: boolean }>`
  display: flex;
  align-items: center;
  border-radius: 8px;
  padding: ${props => (props.$showPadding ? '12px 16px' : '8px')};
  border: ${props => (props.$showBorder ? '1px' : '0')} solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledProductConnected = styled.div`
  background-color: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  border-radius: 8px;
  padding: 16px;
  margin-block-start: 8px;
`;

const StyledWoltLogo = styled(WoltLogo).attrs({ height: 14 })`
  fill: ${cbSemanticCssVars.textTertiary};
`;

const ThumbnailContainer = styled.div`
  width: 60px;
  height: 40px;
  padding: 2px;
  border-radius: 8px;
  background-color: ${cbSemanticCssVars.borderPrimary};
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  margin-inline-end: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const ProductName = styled.div`
  ${typographyCss.Body3()}
  font-weight: 500;
  text-wrap: balance;
`;

const StyledBarcodeTag = styled(BarcodeTag)`
  width: 16px;
  height: 16px;
  margin-inline-end: 8px;
`;

const StyledGtin = styled.div`
  display: flex;
  align-items: center;
`;

const StyledCloseButtonContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
`;

const StyledChainLink = styled(ChainLink)`
  width: 16px;
  height: 16px;
  margin-inline-end: 8px;
`;

const ProductConnectedMessage = styled.div`
  display: flex;
  align-items: start;
`;

const ProductConnectedTitle = styled.div`
  ${typographyCss.Body3()}
  font-weight: 700;
`;

const Body3 = styled.div`
  ${typographyCss.Body3()}
`;

const DisconnectButton = styled(TextButton)`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-block-start: 4px;
`;

const CatalogProductInfo = ({ product, onDisconnect, menuLocalizer, isPreview }: CatalogProductInfoProps) => {
  return (
    <StyledProductContainer $showBorder={!!onDisconnect || isPreview} $showPadding={!!onDisconnect || isPreview}>
      <ThumbnailContainer>
        {product.images[0] ? <img width={60} height={40} src={product.images[0].url} /> : <StyledWoltLogo />}
      </ThumbnailContainer>

      <div>
        <ProductName>{menuLocalizer(product.name)}</ProductName>
        <StyledGtin>
          <StyledBarcodeTag />
          <Body3>{product.gtin}</Body3>
        </StyledGtin>
      </div>
      {onDisconnect && (
        <StyledCloseButtonContainer>
          <IconButton size="tiny" variant="grey" background="staticWhite" onClick={onDisconnect}>
            <Cross />
          </IconButton>
        </StyledCloseButtonContainer>
      )}
    </StyledProductContainer>
  );
};

export const CatalogProduct = ({ sidePanelMode, offer, menuLocalizer, nutritionSettings }: ProductFieldsProps) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { connectedProductId, setConnectedProductId, connectedProduct, loadingConnectedProduct, overrideProductAttr } =
    useProductOverridesContext();
  const [searchValue, setSearchValue] = useState('');
  const form = useForm<FormValues>();
  const { values } = useFormState<FormValues>();
  const { selection } = useSelectionData();
  const scope = useScopeContext();
  const intl = useIntl();

  const {
    data: catalogProducts = [],
    refetch: fetchCatalogProducts,
    isLoading: loadingCatalogProducts,
  } = useQuery(['/search-product/query', searchValue], () => searchCatalogProducts(searchValue, scope), {
    enabled: false,
  });

  const debouncedHandleSearch = useDebounce(fetchCatalogProducts, 300);

  const handleSearch = (search: string) => {
    setSearchValue(search);
    debouncedHandleSearch({ gtin: search });
  };

  const handleSearchClear = () => {
    setSearchValue('');
  };

  const handleClickProduct = (product: Product) => {
    setConnectedProductId(product.id);
    setSearchValue('');

    const transformedData = transformFeedProductToFormValues(product, {
      language: selection.primary_language,
      nutritionSettings,
    });

    const formChanges = getCombinedFormValues(values, transformedData);
    overrideProductAttr(...formChanges.overriddenProductFields);
    form.batch(() => {
      Object.keys(formChanges).forEach((field: keyof ProductFormValues) => {
        form.change(field, formChanges[field] as ProductFormValues[typeof field]);
      });
    });
  };

  const handleDisconnect = () => {
    form.change('productId', undefined);
    setConnectedProductId(undefined);
  };

  const previewComponent = (
    <PreviewRowContainer>
      {offer?.product_id && loadingConnectedProduct && (
        <SpinnerContainer>
          <Spinner size="small" />
        </SpinnerContainer>
      )}
      {offer?.product_id && connectedProduct && (
        <>
          <CatalogProductInfo product={connectedProduct} menuLocalizer={menuLocalizer} isPreview />
          <StyledProductConnected>
            <ProductConnectedMessage>
              <StyledChainLink />
              <div>
                <ProductConnectedTitle>
                  {intl.formatMessage({ id: 'menu-item.catalog-product-section.connected-message' })}
                </ProductConnectedTitle>
                <Body3>{intl.formatMessage({ id: 'menu-item.catalog-product-section.connected-description' })}</Body3>
              </div>
            </ProductConnectedMessage>
          </StyledProductConnected>
        </>
      )}
      {!offer?.product_id && <DisplayText>-</DisplayText>}
    </PreviewRowContainer>
  );
  const editComponent = (
    <>
      {!connectedProduct && (
        <StyledInputContainer>
          <StyledPopover
            showOnHover={false}
            renderArrow={false}
            placement="bottom"
            show={!!searchValue}
            onClickOutside={() => setSearchValue('')}
            content={
              <StyledMenuItemGroup>
                {loadingCatalogProducts && (
                  <SpinnerContainer>
                    <Spinner size="small" />
                  </SpinnerContainer>
                )}
                {catalogProducts.map(product => (
                  <StyledMenuActionItem key={product.id} variant="black" onClick={() => handleClickProduct(product)}>
                    <CatalogProductInfo product={product} menuLocalizer={menuLocalizer} />
                  </StyledMenuActionItem>
                ))}
                {!loadingCatalogProducts && searchValue && catalogProducts.length === 0 && (
                  <Body3>{intl.formatMessage({ id: 'menu-item.catalog-product-section.no-product-message' })}</Body3>
                )}
              </StyledMenuItemGroup>
            }
          >
            <Input
              icon={<MagnifyingGlass />}
              value={searchValue}
              onChange={e => handleSearch(e.target.value)}
              onClearClick={handleSearchClear}
              placeholder={intl.formatMessage({ id: 'menu-item.catalog-product-section.search-input-placeholder' })}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  // Ignore the enter key to prevent form submission
                  e.preventDefault();
                }
              }}
            />
          </StyledPopover>
        </StyledInputContainer>
      )}
      {connectedProduct && (
        <>
          <CatalogProductInfo
            product={connectedProduct}
            onDisconnect={handleDisconnect}
            menuLocalizer={menuLocalizer}
          />
          <StyledProductConnected>
            <ProductConnectedMessage>
              <StyledChainLink />
              <div>
                <ProductConnectedTitle>
                  {intl.formatMessage({ id: 'menu-item.catalog-product-section.connected-message' })}
                </ProductConnectedTitle>
                <Body3>{intl.formatMessage({ id: 'menu-item.catalog-product-section.connected-description' })}</Body3>
              </div>
            </ProductConnectedMessage>
            <DisconnectButton variant="black" onClick={handleDisconnect}>
              {intl.formatMessage({ id: 'menu-item.catalog-product-section.disconnect-button' })}
            </DisconnectButton>
          </StyledProductConnected>
        </>
      )}
    </>
  );

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: 'menu-item.catalog-product' })}
      previewComponent={previewComponent}
      editComponent={editComponent}
      sidePanelMode={sidePanelMode}
      offer={offer}
      shouldDisplayCreateTag={!connectedProductId}
    />
  );
};
